import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { fantasyService } from "@/api/services";
import { Crown, Lightning, ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * SEO-friendly public page rendering a single AI Captain pick for a given
 * FPL gameweek. Two routes hit this component:
 *
 *   /fpl/captain         — auto-resolves to the current gameweek (server side)
 *   /fpl/gw:gw/captain   — locked to a specific gameweek
 *
 * Emits full JSON-LD (Article + SportsEvent) so Google can build a rich
 * snippet for queries like "FPL captain GW38 picks".
 */
export default function FplCaptainOfTheWeek() {
    const { gw: rawGw } = useParams();
    const navigate = useNavigate();
    const gwParam = rawGw ? parseInt(rawGw.replace(/[^0-9]/g, ""), 10) : null;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await fantasyService.captainOfTheWeek(
                    gwParam && gwParam > 0 ? gwParam : undefined
                );
                if (cancelled) return;
                setData(res);
                // If we landed on /fpl/captain (no slug), upgrade the URL to
                // the canonical /fpl/gwN/captain slug so the page is shareable
                // and a single canonical exists per gameweek.
                if (!gwParam && res?.gameweek?.id) {
                    navigate(`/fpl/gw${res.gameweek.id}/captain`, {
                        replace: true,
                    });
                }
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [gwParam, navigate]);

    const gw = data?.gameweek;
    const cap = data?.advice?.captain;
    const diff = data?.advice?.differential;
    const avoid = data?.advice?.avoid;
    const headline = data?.advice?.headline;

    const canonicalPath = gw ? `/fpl/gw${gw.id}/captain` : "/fpl/captain";
    const pageTitle = gw
        ? `FPL Captain ${gw.name} — AI pick`
        : "FPL Captain of the Week — AI pick";
    const description = cap
        ? `KickLoom AI tips ${cap.name} (${cap.team}) as captain for FPL ${gw?.name}. ${headline || cap.reason || ""}`
        : "AI-powered Fantasy Premier League captain pick of the week, with form, fixture and ownership analysis.";

    const jsonLd = cap && gw
        ? {
              "@context": "https://schema.org",
              "@graph": [
                  {
                      "@type": "Article",
                      headline: `FPL Captain ${gw.name}: AI tips ${cap.name}`,
                      description,
                      author: {
                          "@type": "Organization",
                          name: "KickLoom AI",
                          url: "https://kickloom.com",
                      },
                      publisher: {
                          "@type": "Organization",
                          name: "KickLoom",
                          url: "https://kickloom.com",
                      },
                      datePublished: data?.cached_at || new Date().toISOString(),
                      dateModified: data?.cached_at || new Date().toISOString(),
                      mainEntityOfPage: `https://kickloom.com${canonicalPath}`,
                      about: "Fantasy Premier League",
                      keywords: [
                          `FPL captain ${gw.name}`,
                          `FPL ${gw.name} picks`,
                          `Fantasy Premier League ${gw.name}`,
                          `FPL ${cap.name} captain`,
                      ],
                  },
                  {
                      "@type": "SportsEvent",
                      name: `Fantasy Premier League ${gw.name}`,
                      startDate: gw.deadline,
                      sport: "Association football",
                      eventStatus: "https://schema.org/EventScheduled",
                      eventAttendanceMode:
                          "https://schema.org/OnlineEventAttendanceMode",
                      organizer: {
                          "@type": "Organization",
                          name: "Premier League",
                      },
                  },
              ],
          }
        : undefined;

    return (
        <Layout>
            <Seo
                title={pageTitle}
                description={description}
                path={canonicalPath}
                jsonLd={jsonLd}
            />

            <div className="max-w-5xl mx-auto px-4 md:px-8 pt-10">
                <Link
                    to="/"
                    className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-primary transition-snappy"
                >
                    ← Back to KickLoom
                </Link>
                <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mt-6 mb-2">
                    ▌ Fantasy Premier League · AI captain pick
                </div>
                <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter">
                    {gw ? `${gw.name} captain` : "Captain of the week"}
                </h1>
                {gw?.deadline && (
                    <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-2">
                        Deadline ·{" "}
                        {new Date(gw.deadline).toLocaleString(undefined, {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                )}
            </div>

            <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
                {loading ? (
                    <div className="bg-card border border-border p-10 animate-pulse">
                        <div className="h-3 w-32 bg-border mb-4" />
                        <div className="h-12 w-1/2 bg-border" />
                    </div>
                ) : error || !cap ? (
                    <div
                        className="bg-card border border-border p-8 text-zinc-300"
                        data-testid="fpl-cotw-empty"
                    >
                        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
                            ▌ Off-season
                        </p>
                        <p>
                            The AI captain pick isn't available right now. It returns
                            for every Fantasy Premier League gameweek — check back closer
                            to the next deadline.
                        </p>
                    </div>
                ) : (
                    <article
                        className="space-y-4"
                        data-testid="fpl-cotw-article"
                    >
                        {headline && (
                            <div className="bg-card border border-primary/40 p-6 md:p-7 relative overflow-hidden">
                                <Lightning
                                    size={80}
                                    weight="duotone"
                                    className="absolute -right-3 -top-3 text-primary/10"
                                />
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-2">
                                    ▌ Headline
                                </div>
                                <p className="text-lg md:text-xl text-zinc-100 leading-relaxed">
                                    {headline}
                                </p>
                            </div>
                        )}

                        <section
                            className="bg-card border border-primary/40 p-6 md:p-8 relative overflow-hidden"
                            data-testid="fpl-cotw-captain"
                        >
                            <Crown
                                size={160}
                                weight="duotone"
                                className="absolute -right-8 -top-8 text-primary/10"
                            />
                            <div className="relative">
                                <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-primary text-primary mb-3">
                                    Captain
                                </span>
                                <h2 className="text-4xl md:text-6xl font-black tracking-tight">
                                    {cap.name}
                                </h2>
                                <div className="font-mono text-xs uppercase tracking-widest text-zinc-400 mt-2">
                                    {cap.team}
                                    {cap.ownership != null && ` · ${cap.ownership}% owned`}
                                    {cap.form != null && ` · form ${cap.form}`}
                                </div>
                                <p className="text-base md:text-lg text-zinc-200 mt-5 leading-relaxed max-w-3xl">
                                    {cap.reason}
                                </p>
                            </div>
                        </section>

                        {diff && (
                            <section
                                className="bg-card border border-border p-5 md:p-6"
                                data-testid="fpl-cotw-differential"
                            >
                                <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-amber-500/60 text-amber-300 mb-2">
                                    Differential
                                </span>
                                <div className="flex items-baseline justify-between gap-3">
                                    <h3 className="text-2xl font-bold">{diff.name}</h3>
                                    {diff.ownership != null && (
                                        <span className="font-mono text-sm text-amber-300">
                                            {diff.ownership}% owned
                                        </span>
                                    )}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                                    {diff.team}
                                </div>
                                <p className="text-sm md:text-base text-zinc-300 mt-3 leading-relaxed">
                                    {diff.reason}
                                </p>
                            </section>
                        )}

                        {avoid && (
                            <section
                                className="bg-card border border-border p-5 md:p-6"
                                data-testid="fpl-cotw-avoid"
                            >
                                <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-red-500/40 text-red-300 mb-2">
                                    Avoid
                                </span>
                                <h3 className="text-2xl font-bold">{avoid.name}</h3>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                                    {avoid.team}
                                </div>
                                <p className="text-sm md:text-base text-zinc-300 mt-3 leading-relaxed">
                                    {avoid.reason}
                                </p>
                            </section>
                        )}

                        <section className="bg-card border border-border p-6 md:p-7 flex items-start gap-4">
                            <Sparkle
                                size={28}
                                weight="fill"
                                className="text-primary shrink-0 mt-0.5"
                            />
                            <div>
                                <p className="text-base md:text-lg text-zinc-200 leading-relaxed">
                                    Want this analysis personalised for{" "}
                                    <span className="text-primary font-semibold">
                                        your
                                    </span>{" "}
                                    squad — including transfer suggestions, chip strategy
                                    and differentials?
                                </p>
                                <Link
                                    to="/fantasy"
                                    className="inline-flex items-center gap-2 font-bold uppercase tracking-wider text-sm bg-primary text-primary-foreground hover:bg-[#33FFB0] px-4 py-2 mt-4 transition-snappy"
                                    data-testid="fpl-cotw-cta"
                                >
                                    Link your FPL team
                                    <ArrowRight size={14} weight="bold" />
                                </Link>
                            </div>
                        </section>
                    </article>
                )}
            </div>
        </Layout>
    );
}
